import { useBrowseProductEntitlementApi } from '@aibees/shared/api';
import type {
  CatalogFeature,
  CatalogFeatureChild,
  CatalogProduct,
  Plan,
} from '@aibees/shared/definition';
import usePriceAndPlanStore from '@aibees/shared/features/price-and-plan/store';
import { useMemo } from 'react';
import { getColumnKey, matchColumnKey } from '../pricing-cards/catalog';
import { usePlanList } from '../pricing-cards/hooks/use-plan-list';
import type {
  CellValue,
  CompareRow,
  FeatureRow,
  FeatureSection,
  SectionCoverage,
} from './types';

function sortProducts(products: CatalogProduct[]): CatalogProduct[] {
  return [...products].sort((a, b) => {
    const orderA = Number(a.metadata?.order) || 0;
    const orderB = Number(b.metadata?.order) || 0;
    return orderA - orderB;
  });
}

function resolveCellValue(
  feature: CatalogFeature,
  product: CatalogProduct
): CellValue {
  const key = matchColumnKey(product, feature.products ?? []);
  if (!key) return 'dash';
  const cell = feature.cells?.[key];
  if (!cell) return 'dash';

  if (cell.comingSoon || cell.valuesPerPlan?.comingSoon) return 'comingSoon';

  const value = cell.valuesPerPlan?.value;
  if (value) {
    if (value.trim().toLowerCase() === 'coming soon') return 'comingSoon';
    return value;
  }

  return 'check';
}

// Per §3, a collapsible child now carries a value per plan column, read from
// child.cells[k] keyed by product.name (was a single child.cell). Coming-soon
// and other flags come from that same per-column cell.
function resolveChildValue(
  child: CatalogFeatureChild,
  product: CatalogProduct
): CellValue {
  const presenceKey = matchColumnKey(product, child.products ?? []);
  if (!presenceKey) return 'dash';

  const cellKey =
    matchColumnKey(product, Object.keys(child.cells ?? {})) ?? presenceKey;
  const cell = child.cells?.[cellKey];

  if (cell?.comingSoon || cell?.valuesPerPlan?.comingSoon) return 'comingSoon';

  const value = cell?.valuesPerPlan?.value;
  if (value) {
    if (value.trim().toLowerCase() === 'coming soon') return 'comingSoon';
    return value;
  }

  return 'check';
}

function buildRow(
  feature: CatalogFeature,
  products: CatalogProduct[]
): FeatureRow {
  const values: Record<string, CellValue> = {};
  const subtexts: Record<string, string> = {};
  for (const product of products) {
    const columnKey = getColumnKey(product);
    values[columnKey] = resolveCellValue(feature, product);
    const cellKey = matchColumnKey(product, Object.keys(feature.cells ?? {}));
    const subtext = cellKey
      ? feature.cells?.[cellKey]?.valuesPerPlan?.subtext
      : undefined;
    if (subtext) subtexts[columnKey] = subtext;
  }
  const badge = feature.metadata?.badge;

  return {
    id: feature.metadata.code || feature.key,
    feature: feature.name,
    badge: badge || undefined,
    values,
    subtexts,
  };
}

function buildSection(
  feature: CatalogFeature,
  products: CatalogProduct[]
): FeatureSection {
  const productCounts = feature.productCounts ?? {};
  // Per §3: the collapsible counter total is max(productCounts), NOT
  // children.length — productCounts already excludes coming-soon sub-features,
  // so children.length would over-count (e.g. "of 15" instead of "of 14").
  const total = Math.max(0, ...Object.values(productCounts));

  const headerValues: Record<string, CellValue> = {};
  const headerCoverage: Record<string, SectionCoverage> = {};

  for (const product of products) {
    const columnKey = getColumnKey(product);
    const countKey = matchColumnKey(product, Object.keys(productCounts));
    const filled = countKey ? productCounts[countKey] ?? 0 : 0;
    const cellKey = matchColumnKey(product, Object.keys(feature.cells ?? {}));
    const unit = cellKey ? feature.cells?.[cellKey]?.countUnit : undefined;
    headerCoverage[columnKey] = { filled, total, unit };
    headerValues[columnKey] = filled > 0 ? 'check' : 'dash';
  }

  // Children arrive pre-sorted by `order`; render them in array order.
  const featureRows: FeatureRow[] = (feature.children ?? []).map((child) => {
    const values: Record<string, CellValue> = {};
    for (const product of products) {
      values[getColumnKey(product)] = resolveChildValue(child, product);
    }
    return {
      id: child.id || child.key,
      feature: child.name,
      values,
    };
  });

  return {
    id: feature.metadata.code || feature.key,
    title: feature.name,
    headerValues,
    headerCoverage,
    features: featureRows,
  };
}

function groupPlansByProductId(
  productCards: { product: { id: string }; plans: Plan[] }[]
): Record<string, Plan[]> {
  const grouped: Record<string, Plan[]> = {};
  for (const entry of productCards) {
    grouped[entry.product.id] = entry.plans;
  }
  return grouped;
}

export function useCollapsedTableData() {
  const yearly = usePriceAndPlanStore.useYearly();
  const { data, isLoading: catalogLoading } = useBrowseProductEntitlementApi();
  const { productCards, isLoading: plansLoading } = usePlanList(yearly);

  const isLoading = catalogLoading || plansLoading;

  return useMemo(() => {
    const catalogProducts: CatalogProduct[] = data?.products ?? [];
    const catalogFeatures: CatalogFeature[] = data?.features ?? [];

    // Same gate as the card grid (use-plan-list): only public products become
    // columns, so the compare table and the cards show the same plan set.
    const products = sortProducts(
      catalogProducts.filter(
        (p) => p.active && p.metadata?.is_public === 'true'
      )
    );

    // Render features in array order (already sorted server-side by
    // compareOrder) — no client sort/group. Each feature is a collapsible
    // section when it has children, otherwise a flat row.
    const rows: CompareRow[] = [];
    for (const feature of catalogFeatures) {
      if ((feature.children?.length ?? 0) > 0) {
        rows.push({
          kind: 'section',
          section: buildSection(feature, products),
        });
      } else if (feature.metadata.section) {
        rows.push({ kind: 'row', row: buildRow(feature, products) });
      }
    }

    return {
      products,
      rows,
      isLoading,
      plansByProduct: groupPlansByProductId(productCards),
      yearly,
    };
  }, [data, isLoading, productCards, yearly]);
}
